/**
 * Catalog loading. The catalog is immutable published data: fetched once,
 * validated with zod once, then held in a plain module variable and handed to
 * React through context. It is deliberately NOT in a store — nothing ever
 * mutates it, so nothing should subscribe to it.
 */

import type { Catalog, Entry, EntryId, Section } from "@schema/catalog.ts";
import { catalogSchema } from "@schema/catalog.zod.ts";

let cached: Catalog | null = null;
let inflight: Promise<Catalog> | null = null;

export function cachedCatalog(): Catalog | null {
  return cached;
}

/**
 * Swap the module-level catalog after a confirmed update.
 *
 * The catalog is otherwise immutable-by-construction, and this is the single
 * sanctioned exception: a newly published catalog of the same schema version,
 * already validated by `refetchCatalog`. `inflight` is cleared so a concurrent
 * first load can't resolve to the version we just replaced.
 */
export function replaceCachedCatalog(next: Catalog): void {
  cached = next;
  inflight = null;
}

export function loadCatalog(): Promise<Catalog> {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = fetch(`${import.meta.env.BASE_URL}catalog/v1/catalog.json`)
    .then((res) => {
      if (!res.ok) throw new Error(`catalog fetch failed: HTTP ${res.status}`);
      return res.json();
    })
    .then((data) => {
      const parsed = catalogSchema.parse(data);
      cached = parsed;
      return parsed;
    })
    .catch((err) => {
      inflight = null; // allow a retry after a transient failure
      throw err;
    });
  return inflight;
}

export interface SectionWithEntries {
  section: Section;
  /** The entries to render — the surviving ones when a `visible` set is given. */
  entries: Entry[];
  /** How many entries this section has in the full catalog, filters ignored. */
  total: number;
}

/**
 * Entries grouped by section, both in road order. Pure; safe to memoize.
 *
 * Pass `visible` (from `applyFilters`) to render a filtered road: entries
 * outside the set are dropped and sections left with nothing are omitted
 * entirely, so filtering collapses the page instead of leaving a run of empty
 * headers behind.
 */
export function groupBySection(
  catalog: Catalog,
  visible?: ReadonlySet<EntryId>,
): SectionWithEntries[] {
  const groups: SectionWithEntries[] = [];
  for (const section of catalog.sections) {
    const all = catalog.entries.filter((e) => e.sectionId === section.id);
    const entries = visible ? all.filter((e) => visible.has(e.id)) : all;
    if (entries.length === 0 && visible) continue;
    groups.push({ section, entries, total: all.length });
  }
  return groups;
}
